import { getCSS, getCSSVariableValue } from "./_helpers";
import ApexCharts, { ApexOptions } from "apexcharts";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { application } from "../../_runtime/bootstrap";
import { HttpResponse } from "@dirootie-core/http-types";

type Props = {
  id: string;
  subTitle: string;
  seriesName: string;
  urlEndpoint: string;
  yAxisProperty: string;
  dateFormat?: string;
  height?: string;
};

type ChartSeriesData = {
  x: string;
  y: number;
};

const getOneMonthBeforeDate = () => {
  const currentDate = new Date();

  return new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );
};

function formatDate(date: Date): string {
    const pad = (num: number) => num.toString().padStart(2, '0');

    const month = pad(date.getMonth() + 1);  // getMonth() is zero-indexed, add 1 to make it 1-indexed
    const day = pad(date.getDate());
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
}


export const LineChart: FC<Props> = ({
  id,
  subTitle,
  seriesName,
  urlEndpoint,
  yAxisProperty,
  dateFormat = "dd/MM/yy",
  height = "275px",
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartReference = useMemo(() => chartRef, [chartRef]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<ChartSeriesData[]>([]);
  const currentDate = new Date();

  const refreshMode = () => {
    if (!chartReference.current) {
      return;
    }

    const height = parseInt(getCSS(chartReference.current, "height"));

    const chart = new ApexCharts(
      chartReference.current,
      getChartOptions(height, chartData, seriesName, dateFormat)
    );
    if (chart) {
      chart.render();
    }

    return chart;
  };

  useEffect(() => {
    let query: string = `?startDate=${formatDate(currentDate)}&endDate=${formatDate(getOneMonthBeforeDate())}`;
    application
      .getHttpClient()
      .get(urlEndpoint + query)
      .then((res: HttpResponse) => {
        const mappedArray = res.body.map((item: any) => ({
          x: item.date,
          y: item[yAxisProperty],
        }));

        setIsLoading(false);
        setTimeout(() => setChartData(mappedArray), 100);
      })
      .catch(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const chart = refreshMode();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartData]);

  return (
    <div className="px-3 pt-3">
      {!isLoading && <div id={id} ref={chartRef} style={{ height }}></div>}
    </div>
  );
};

function getChartOptions(
  height: number,
  data: ChartSeriesData[],
  seriesName: string,
  dateFormat: string
): ApexOptions {
  var primaryColor = getCSSVariableValue("--bs-primary");
  var secondaryColor = getCSSVariableValue("--bs-secondary");

  return {
    chart: {
      type: "line",
      height: height,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: true,
      },
    },
    grid: {
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val.toFixed(0);
        },
      },
    },
    xaxis: {
      type: "datetime",
      tooltip: {
        enabled: false,
      },
      labels: {
        format: dateFormat,
        datetimeUTC: false,
      },
      tickAmount: 8,
    },
    series: [
      {
        name: seriesName,
        data,
      },
    ],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "straight",
      width: 3,
    },
    markers: {
      strokeWidth: 3,
      size: 5,
    },
    colors: [primaryColor, secondaryColor],
  };
}
